/**
 * Course-aware Extractor Prompt Builder & JSON Import Validator.
 * Matches backend unified prompt template and pedagogical rules.
 */

export const DEFAULT_PROFILES: Record<string, string> = {
  LANGUAGE:
    "- Grammar and usage: tenses, conditionals, modals, voice, subject-verb agreement, sentence correction, and error identification.\n" +
    "- Vocabulary in context: collocations, connotation vs denotation, word forms, prefixes, suffixes, and contextual guessing.\n" +
    "- Reading comprehension: short passages placed inside the stem or linked via passageKey testing main idea, inference, reference, and factual reasoning.\n" +
    "- Study and language skills: note-taking scenarios, paragraph organization, summary completion, and cohesive devices.\n" +
    "- Distractors: realistic learner errors (wrong tense, agreement error, preposition/article slips, wrong word form).",

  COMPUTATIONAL:
    "- Computational rigor: every question makes the student compute, solve, derive, or evaluate formulas.\n" +
    "- Solve step by step BEFORE writing options; verify correct numerical answer.\n" +
    "- Consistent units: specify clean numbers and state units clearly; check dimensional consistency.\n" +
    "- Realistic distractors: compute wrong options from common student slips (sign error, unit conversion slip, missing square/factor, wrong formula).\n" +
    "- LaTeX formatting: format all equations, variables, and units using LaTeX ($...$). Explain the key step in the explanation.",

  FACTUAL:
    "- Application of facts: at least half the questions require reasoning from facts (cause-and-effect, applying a principle to a new situation, timeline/process interpretation).\n" +
    "- No trivial recall: avoid obscure trivia, isolated dates, or minor names unless the source text heavily stresses them.\n" +
    "- Compare and contrast: distinguish related historical, geographic, or biological facts and classifications.\n" +
    "- Distractors: plausible true statements that answer a different question, or concepts from an adjacent topic.",

  CONCEPTUAL:
    "- Scenario-based thinking: apply concepts, theories, models, or legal/ethical principles to realistic case scenarios.\n" +
    "- Contrast look-alikes: test the boundary between two closely related concepts or theories; avoid verbatim definition matching.\n" +
    "- Deep reasoning: students must analyze why a concept applies, not merely recognize its name.\n" +
    "- Distractors: valid alternative concepts from the same course that students frequently confuse with the target concept.",

  PROCEDURAL:
    "- Step-by-step procedures: test correct order of operations, lab protocols, algorithm execution, or diagnostic procedures.\n" +
    "- Consequence analysis: ask what happens if a step is omitted, reordered, or performed incorrectly.\n" +
    "- Concrete scenarios: describe clear setup conditions so exactly one procedure or outcome is standard and valid.\n" +
    "- Distractors: plausible sequences with one swapped, missing, or erroneous step.",
};

export const FLEN_1011_CUSTOM_PROFILE =
  "- Grammar & Mechanics: Tenses (present perfect vs past simple, past continuous), conditionals (types 0, 1, 2, 3), active and passive voice, modal verbs of obligation/permission/probability, subject-verb agreement with quantifiers/compound subjects, and punctuation in complex sentences.\n" +
  "- Vocabulary in Context: Guessing meanings from contextual clues, academic word list, prefixes/suffixes, collocations, synonyms/antonyms, and connotative vs denotative meanings.\n" +
  "- Reading Comprehension & Study Skills: Short passages in the stem testing main idea, author's tone/purpose, inference, reference (pronoun antecedents), Cornell note-taking, skimming, scanning, SQ3R method, and dictionary usage guide words.\n" +
  "- Writing & Paragraph Skills: Topic sentences, supporting details, transitional words/cohesive devices, unity and coherence in paragraphs.";

export const COURSE_TYPES = ["LANGUAGE", "COMPUTATIONAL", "FACTUAL", "CONCEPTUAL", "PROCEDURAL"] as const;
export type CourseType = typeof COURSE_TYPES[number];

export const BASE_EXTRACTOR_PROMPT_TEMPLATE = `# ROLE
You are an expert Ethiopian university tutor, examination setter and curriculum editor for first-year (freshman) students. For ONE chapter you write study notes, an exam-style multiple-choice question bank, or both, depending on MODE. Notes and questions always share the same sections, so every question can be answered from the notes.

# INPUT
COURSE: {{COURSE_NAME_AND_CODE}}
COURSE TYPE: {{COURSE_TYPE}}
CHAPTER: {{CHAPTER}}
MODE: {{MODE}}
QUESTIONS PER LEVEL: {{QUESTIONS_PER_LEVEL}}   (used only when MODE is QUIZ or BOTH)
LEVELS: {{LEVELS}}   (which levels to write in this run; use one level to split a big job into parts)
SECTIONS: {{SECTIONS}}
ALREADY COVERED: {{ALREADY_COVERED}}
SOURCE: the attached module/PDF. If text is not pasted below, read the attached file itself. If you truly cannot read it, output {"error":"source not readable"} and nothing else. Never invent content.
{{EXAM_SECTION}}

# MODE RULES (read first)
- MODE = NOTES: write only Part A (notes). Output kind "notes". Do not write questions.
- MODE = QUIZ: write only Part B (quiz). Output kind "quiz". Do not write notes. Still use the module's section titles as topicTitle.
- MODE = BOTH: write Part A and Part B. Output kind "bundle". Write the notes first, then write every question so it can be answered from those notes.
In every mode the section titles come from the module, so notes and questions made in separate runs still match.

# 1. SECTIONS (shared by notes and quiz)
- Detect the chapter's own numbered sections, in order, and use the module's numbering and titles EXACTLY (e.g. "2.2 Grammar focus: Conditionals"). If SECTIONS is set, work only on those.
- Ignore cover pages, contents, objectives lists, headers, footers, page numbers, and activity instructions that only say "discuss with a partner" or "tell your teacher". Keep teaching content, rules, definitions, examples and reading themes.
- Use ONLY the module's content. No outside facts. If something is unclear or missing, write only what is supported and add a reviewFlag "section title: issue". Never guess.
- "topicTitle" and "chapterTitle" must be written identically for the same section in notes and in questions, in every mode and every run. Do not rename, merge unrelated sections or invent topics.

# 2. PART A: NOTES (only if MODE is NOTES or BOTH; one note per section)
- Plain English (B1-B2), short sentences, every term defined on first use.
- Explain WHY: the reason behind each rule and when it applies.
- No filler or introductions. Never write "the text", "the module", "the passage" or "see the figure".
- Maximum 350 words per note, not counting examples.
- Math and symbols in LaTeX ($...$, $$...$$). Plain Markdown only: no emojis, HTML or colors.
- Adapt to course type: LANGUAGE = forms, rules, contrasts between look-alike items, labelled example sentences, common errors. COMPUTATIONAL = formulas with units, step-by-step solving, typical numeric slips. FACTUAL = key facts, cause and effect. CONCEPTUAL = concept in plain words, applied case, comparisons. PROCEDURAL = ordered steps, what goes wrong if one is skipped.
Layout of each "markdown" (same order, skip a part only if it has nothing to say, use ### subheadings, do not repeat the topic title):
### In simple words
2-3 sentences, everyday words, no jargon.
### How it works
The reasoning in short bullets.
### Rules / Forms   (use "Formulas" for computational courses)
Each rule clearly stated, with when to use it and one labelled example per use.
### Worked example
Prefer an example from the module. If you build one, use only the module's own rules and add a reviewFlag "section title: constructed example, please verify". Show the wrong version, the corrected version and the rule that fixes it (language), or Given / Find / Idea / Solution in numbered steps / Check (computational).
### Exam focus
Three lines naming the skill tested at each level for this section:
- **Easy question looks like:** ...
- **Medium question looks like:** ...
- **Hard question looks like:** ...
### Exam traps
Up to 3 numbered traps: look-alike structures, typical wrong choices, easily mistaken terms.
### Check yourself
One why/what-if question with a one-line answer.
Key terms: 3 to 10 per note in "keyTerms", with concise definitions taken from the module. Use the same wording in the quiz. Do not repeat them in the Markdown.

# 3. PART B: QUIZ (only if MODE is QUIZ or BOTH; multiple choice)
Quantity (MANDATORY): for EACH level listed in LEVELS, write exactly QUESTIONS PER LEVEL questions for the chapter (or for the SECTIONS selected). Spread each level across all sections in proportion to importance. No two questions test the same idea, none rewords another, none repeats ALREADY COVERED. If ideas run short, vary the question format; never go below the count.
Difficulty labels (exact): "Easy", "Medium", "Hard".
- Easy: recall or recognise a core idea, one step.
- Medium: apply a rule to a new simple case, fix a sentence, or contrast two related ideas, about two steps.
- Hard: multi-step reasoning, a realistic scenario, a short reading passage with inference, or telling apart two close misconceptions.
Rate by the thinking needed, not by stem length.
Formats by course type: LANGUAGE = grammar and usage, error identification, sentence correction, dialogue completion, vocabulary in context, collocations, connotation vs denotation, short reading passages placed inside the stem (main idea, inference, reference words), study-skills scenarios. COMPUTATIONAL = every question makes the student compute; solve step by step BEFORE writing options; wrong options from realistic slips (sign, unit, wrong formula). FACTUAL = reason from facts, no trivia. CONCEPTUAL = scenarios, compare/contrast, no verbatim definitions. PROCEDURAL = ordered steps, what happens if a step is skipped.
Question rules:
- Self-contained stem. Never write "the text", "the module", "the passage", "paragraph 2" or "see the figure". Any passage needed goes inside the stem.
- Exactly 4 plain-string options (no "A)" prefixes), exactly 1 unambiguously correct.
- Distractors reflect real student mistakes for that exact concept, similar in length and grammar. No silly options, no "all/none of the above", no "both A and B".
- Avoid negative stems; if unavoidable, **bold** the negation.
- Spread the correct position evenly across 0, 1, 2, 3.
- No questions that need an image or graph.
- Explanation: 1-2 short sentences saying why the answer is right and which rule applies.
- Solve each question yourself before choosing "correct". Questions must be answerable from the module's section they come from.
- Math and symbols in LaTeX. Plain Markdown only. English level B1-B2.

# 4. COURSE PROFILE
{{COURSE_PROFILE_BLOCK}}

# 5. SELF-CHECK (silently, fix problems, do not report them)
(a) Every fact, rule, term and example is in the module. (b) Every grammar example and answer is correct by the rule stated. (c) Only the parts required by MODE are present. (d) Notes cover every selected section once, with no duplicates. (e) topicTitle and chapterTitle match the module's numbering and are identical between notes and questions. (f) Question totals are exactly QUESTIONS PER LEVEL for each listed level. (g) One defensible answer per question; "correct" matches the explanation. (h) Correct positions are balanced. (i) A freshman could follow every note. (j) JSON is valid, backslashes doubled, line breaks as \\n.

# 6. OUTPUT
ONLY one valid JSON object, nothing else. Escape each LaTeX backslash as \\\\ and line breaks as \\n. No raw line breaks or unescaped double quotes inside strings. Notes in section order; questions grouped by level (Easy, then Medium, then Hard).
Common fields in every mode:
  "courseName": "{{COURSE_NAME_AND_CODE}}", "courseId": "", "moduleId": "", "chapterId": "",
  "chapterTitle": "{{CHAPTER}}", "status": "draft", "replaceExisting": false, "reviewFlags": []

MODE = NOTES:
{
  "kind": "notes",
  "courseName": "{{COURSE_NAME_AND_CODE}}", "courseId": "", "moduleId": "", "chapterId": "",
  "chapterTitle": "{{CHAPTER}}", "status": "draft", "replaceExisting": false, "reviewFlags": [],
  "notes": [
    {
      "topicId": null,
      "topicTitle": "2.2 Grammar focus: Conditionals",
      "markdown": "### In simple words\\n...",
      "keyTerms": [ { "term": "", "definition": "" } ],
      "sourceReference": "<resource name>, Unit 2, pp. 24-26"
    }
  ]
}

MODE = QUIZ:
{
  "kind": "quiz",
  "courseName": "{{COURSE_NAME_AND_CODE}}", "courseId": "", "moduleId": "", "chapterId": "",
  "chapterTitle": "{{CHAPTER}}", "status": "draft", "replaceExisting": false, "reviewFlags": [],
  "subject": "",
  "difficulty": "Medium",
  "batchId": "batch-<chapter>-<levels>-01",
  "questions": [
    {
      "question": "",
      "options": ["", "", "", ""],
      "correct": 0,
      "difficulty": "Easy",
      "explanation": "",
      "chapterTitle": "{{CHAPTER}}",
      "topicTitle": "2.2 Grammar focus: Conditionals",
      "sourceReference": "<resource name>, Unit 2, pp. 24-26",
      "type": "multiple_choice"
    }
  ]
}

MODE = BOTH:
{
  "kind": "bundle",
  "courseName": "{{COURSE_NAME_AND_CODE}}", "courseId": "", "moduleId": "", "chapterId": "",
  "chapterTitle": "{{CHAPTER}}", "status": "draft", "replaceExisting": false, "reviewFlags": [],
  "subject": "",
  "difficulty": "Medium",
  "batchId": "batch-<chapter>-<levels>-01",
  "notes": [ ...same note objects as in MODE = NOTES... ],
  "questions": [ ...same question objects as in MODE = QUIZ... ]
}`;

export interface ExtractorCourseInput {
  name: string;
  code?: string | null;
  course_type?: string | null;
  courseType?: string | null;
  extraction_profile?: string | null;
  extractionProfile?: string | null;
}

export interface ExtractorExamInput {
  title?: string | null;
  text?: string | null;
}

export interface ExtractorOptionsInput {
  mode?: "NOTES" | "QUIZ" | "BOTH" | "PAST_EXAM" | string | null;
  chapter?: string | null;
  chapterTitle?: string | null;
  questions_per_level?: number | string | null;
  questionsPerLevel?: number | string | null;
  count_per_level?: number | string | null;
  levels?: string | null;
  sections?: string | null;
  already_covered?: string | null;
  alreadyCovered?: string | null;
  from?: number | string | null;
  to?: number | string | null;
  exam?: string | null;
}

/**
 * Builds the full extractor prompt filling placeholders and resolving profiles:
 * (1) course custom profile, (2) default for course type, (3) fallback: ""
 */
export function buildExtractorPrompt(
  course: ExtractorCourseInput,
  exam?: string | ExtractorExamInput | null,
  options?: ExtractorOptionsInput
): string {
  const opts = options || {};

  const name = course.name || "Course";
  const code = course.code ? course.code.trim() : "";
  const courseDisplay = code ? `${name} (${code})` : name;

  const rawType = (course.courseType || course.course_type || "LANGUAGE").toUpperCase().trim();
  const courseType = DEFAULT_PROFILES[rawType] ? rawType : "LANGUAGE";

  const customProfile = (course.extractionProfile || course.extraction_profile || "").trim();

  // Profile resolution order: (1) custom profile, (2) default profile for course type, (3) fallback: ""
  let profileBlock = "";
  if (customProfile) {
    profileBlock = customProfile;
  } else if (DEFAULT_PROFILES[courseType]) {
    profileBlock = DEFAULT_PROFILES[courseType];
  }

  let examTitle = "";
  let examText = "";
  if (typeof exam === "string") {
    examTitle = exam.trim();
  } else if (exam) {
    examTitle = (exam.title || "").trim();
    examText = (exam.text || "").trim();
  }
  if (!examTitle) {
    examTitle = "University Examination";
  }

  const chapterVal = (opts.chapter || opts.chapterTitle || examTitle || "Chapter / Unit").trim();

  const rawMode = String(opts.mode || "QUIZ").trim().toUpperCase();
  const modeVal =
    rawMode === "NOTES" ? "NOTES" :
    rawMode === "BOTH" || rawMode === "BUNDLE" ? "BOTH" :
    rawMode === "PAST_EXAM" || rawMode === "PAST" || rawMode === "EXAM" ? "PAST_EXAM" :
    "QUIZ";

  const qPerLevel = String(
    opts.questions_per_level ||
    opts.questionsPerLevel ||
    opts.count_per_level ||
    "25"
  ).trim();

  const rawLevels = opts.levels;
  const levelsVal = Array.isArray(rawLevels)
    ? rawLevels.map((x: any) => String(x).trim()).filter(Boolean).join(", ")
    : String(rawLevels || "Easy, Medium, Hard").trim();
  let sectionsVal = String(opts.sections || "").trim();

  const fromQ = opts.from !== undefined && opts.from !== null ? String(opts.from).trim() : "";
  const toQ = opts.to !== undefined && opts.to !== null ? String(opts.to).trim() : "";

  if (!sectionsVal && (fromQ || toQ)) {
    if (fromQ && toQ) {
      sectionsVal = `Questions ${fromQ} to ${toQ}`;
    } else if (fromQ) {
      sectionsVal = `From question ${fromQ}`;
    } else if (toQ) {
      sectionsVal = `Up to question ${toQ}`;
    }
  }

  const alreadyCov = String(opts.already_covered || opts.alreadyCovered || "").trim();

  if (!examText && opts.exam) {
    examText = opts.exam.trim();
  }

  let examSection = "";
  if (examText) {
    examSection = `\n\n---\n# SOURCE MATERIAL\n${examText.slice(0, 60000)}`;
  }

  let result = BASE_EXTRACTOR_PROMPT_TEMPLATE;
  result = result.replace(/\{\{COURSE_NAME_AND_CODE\}\}/g, courseDisplay);
  result = result.replace(/\{\{COURSE_TYPE\}\}/g, courseType);
  result = result.replace(/\{\{LANGUAGE \| COMPUTATIONAL \| FACTUAL \| CONCEPTUAL \| PROCEDURAL\}\}/g, courseType);
  result = result.replace(/\{\{CHAPTER\}\}/g, chapterVal);
  result = result.replace(/\{\{one chapter or unit, e.g. "Unit 2: Health and Fitness"\}\}/g, chapterVal);
  result = result.replace(/\{\{MODE\}\}/g, modeVal);
  result = result.replace(/\{\{NOTES \| QUIZ \| BOTH\}\}/g, modeVal);
  result = result.replace(/\{\{QUESTIONS_PER_LEVEL\}\}/g, qPerLevel);
  result = result.replace(/\{\{QUESTIONS PER LEVEL\}\}/g, qPerLevel);
  result = result.replace(/\{\{25\}\}/g, qPerLevel);
  result = result.replace(/\{\{LEVELS\}\}/g, levelsVal);
  result = result.replace(/\{\{Easy, Medium, Hard\}\}/g, levelsVal);
  result = result.replace(/\{\{SECTIONS\}\}/g, sectionsVal);
  result = result.replace(/\{\{blank = all sections of the chapter, or e.g. "2.1-2.3"\}\}/g, sectionsVal);
  result = result.replace(/\{\{ALREADY_COVERED\}\}/g, alreadyCov);
  result = result.replace(/\{\{ALREADY COVERED\}\}/g, alreadyCov);
  result = result.replace(/\{\{optional: question stems or topics to avoid\}\}/g, alreadyCov);
  result = result.replace(/\{\{COURSE_PROFILE_BLOCK\}\}/g, profileBlock);
  result = result.replace(/\{\{EXAM_NAME_YEAR_SEMESTER\}\}/g, examTitle);
  result = result.replace(/\{\{EXAM_SECTION\}\}/g, examSection);
  result = result.replace(/\{\{EXAM\}\}/g, examText);
  result = result.replace(/\{\{FROM\}\}/g, fromQ);
  result = result.replace(/\{\{TO\}\}/g, toQ);

  // Clean leftover {{...}} placeholders and empty lines
  result = result.replace(/\{\{[^}]+\}\}/g, "");
  result = result.replace(/\n{3,}/g, "\n\n");

  return result.trim();
}

/**
 * Extracts first valid JSON object from string, stripping markdown fences if present.
 */
export function extractFirstJsonObject(raw: string): { data: any | null; error: string | null } {
  if (!raw || !raw.trim()) {
    return { data: null, error: "Empty content." };
  }
  const text = raw.trim();

  // 1. Direct parse
  try {
    const direct = JSON.parse(text);
    if (typeof direct === "object" && direct !== null) {
      return { data: direct, error: null };
    }
  } catch {
    // continue to fences
  }

  // 2. Fences
  const fenceRegex = /```(?:json)?\s*([\s\S]*?)\s*```/gi;
  let match: RegExpExecArray | null;
  while ((match = fenceRegex.exec(text)) !== null) {
    try {
      const candidate = JSON.parse(match[1].trim());
      if (typeof candidate === "object" && candidate !== null) {
        return { data: candidate, error: null };
      }
    } catch {
      // try next
    }
  }

  // 3. Outermost braces
  const firstBrace = text.indexOf("{");
  const lastBrace = text.lastIndexOf("}");
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    const candidate = text.slice(firstBrace, lastBrace + 1);
    try {
      const parsed = JSON.parse(candidate);
      if (typeof parsed === "object" && parsed !== null) {
        return { data: parsed, error: null };
      }
    } catch (e: any) {
      return { data: null, error: `Invalid JSON syntax: ${e.message}` };
    }
  }

  return { data: null, error: "No valid JSON object found in text." };
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  reviewFlags: string[];
  questionsCount: number;
  notesCount: number;
  data: any | null;
}

/**
 * Validates imported JSON payload per question number.
 */
export function validateImportJson(input: string | any): ValidationResult {
  let data: any = input;
  if (typeof input === "string") {
    const extracted = extractFirstJsonObject(input);
    if (!extracted.data) {
      return {
        valid: false,
        errors: [extracted.error || "Could not parse JSON."],
        warnings: [],
        reviewFlags: [],
        questionsCount: 0,
        notesCount: 0,
        data: null,
      };
    }
    data = extracted.data;
  }

  const errors: string[] = [];
  const warnings: string[] = [];
  const reviewFlags: string[] = [];

  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return {
      valid: false,
      errors: ["Top level payload must be a JSON object."],
      warnings: [],
      reviewFlags: [],
      questionsCount: 0,
      notesCount: 0,
      data,
    };
  }

  // Top level reviewFlags
  if (Array.isArray(data.reviewFlags)) {
    data.reviewFlags.forEach((f: any) => {
      if (typeof f === "string" && f.trim()) reviewFlags.push(f.trim());
    });
  }

  // Passages
  const validPassageKeys = new Set<string>();
  if (data.passages !== undefined && data.passages !== null) {
    if (!Array.isArray(data.passages)) {
      errors.push("'passages' must be an array of objects if provided.");
    } else {
      data.passages.forEach((p: any, pIdx: number) => {
        if (typeof p === "object" && p !== null) {
          if (typeof p.key === "string" && p.key.trim()) {
            validPassageKeys.add(p.key.trim());
          } else {
            errors.push(`Passage #${pIdx + 1} is missing a non-empty 'key' string.`);
          }
        } else {
          errors.push(`Passage #${pIdx + 1} must be an object.`);
        }
      });
    }
  }

  const kind = String(data.kind || "").trim().toLowerCase();
  const rawQs = data.questions;
  const rawNotes = data.notes;

  const isNotesOnly = kind === "notes" || (rawNotes !== undefined && rawQs === undefined);
  const isBundle = kind === "bundle" || (rawNotes !== undefined && rawQs !== undefined);

  let notesCount = 0;
  if (rawNotes !== undefined || isNotesOnly || isBundle) {
    if (!Array.isArray(rawNotes) || rawNotes.length === 0) {
      if (isNotesOnly || isBundle) {
        errors.push("Top level must contain a non-empty 'notes' array.");
      }
    } else {
      notesCount = rawNotes.length;
      rawNotes.forEach((n: any, nIdx: number) => {
        const nNum = nIdx + 1;
        if (typeof n !== "object" || n === null) {
          errors.push(`Note #${nNum} must be an object.`);
          return;
        }
        const tTitle = n.topicTitle || n.topic_title;
        if (typeof tTitle !== "string" || !tTitle.trim()) {
          errors.push(`Note #${nNum}: 'topicTitle' must be a non-empty string.`);
        }
        const md = n.markdown || n.content;
        if (typeof md !== "string" || !md.trim()) {
          errors.push(`Note #${nNum}: 'markdown' must be a non-empty string.`);
        }
      });
    }
  }

  if (!isNotesOnly) {
    if (!Array.isArray(rawQs) || rawQs.length === 0) {
      errors.push("Top level must contain a non-empty 'questions' array.");
      return {
        valid: false,
        errors,
        warnings,
        reviewFlags,
        questionsCount: 0,
        notesCount,
        data,
      };
    }

    const letterPrefixPattern = /^[A-Da-d][\)\.]\s*/;

    rawQs.forEach((q: any, idx: number) => {
      const qNum = idx + 1;
      if (typeof q !== "object" || q === null) {
        errors.push(`Question #${qNum} must be an object.`);
        return;
      }

      // Stem
      if (typeof q.question !== "string" || !q.question.trim()) {
        errors.push(`Question #${qNum}: 'question' stem must be a non-empty string.`);
      }

      // Options
      if (!Array.isArray(q.options)) {
        errors.push(`Question #${qNum}: 'options' must be an array.`);
      } else {
        const cleaned = q.options.map((o: any) => (o !== null && o !== undefined ? String(o).trim() : ""));
        const nonEmpties = cleaned.filter((o: string) => o.length > 0);
        const isTF =
          nonEmpties.length === 2 &&
          new Set(nonEmpties.map((o: string) => o.toLowerCase())).has("true") &&
          new Set(nonEmpties.map((o: string) => o.toLowerCase())).has("false");

        if (nonEmpties.length !== 4 && !isTF) {
          errors.push(
            `Question #${qNum}: 'options' must contain exactly 4 non-empty strings (or exactly 2 for True/False).`
          );
        }

        // Check warnings: letter prefix & duplicates
        const seen = new Set<string>();
        nonEmpties.forEach((optStr: string, optIdx: number) => {
          if (letterPrefixPattern.test(optStr)) {
            warnings.push(
              `Question #${qNum}, option ${optIdx + 1}: Starts with a letter prefix ('${optStr.slice(0, 12)}...').`
            );
          }
          const lower = optStr.toLowerCase();
          if (seen.has(lower)) {
            warnings.push(`Question #${qNum}: Has duplicate options ('${optStr.slice(0, 20)}').`);
          }
          seen.add(lower);
        });
      }

      // Correct index
      if (typeof q.correct !== "number" || !Number.isInteger(q.correct)) {
        errors.push(`Question #${qNum}: 'correct' must be an integer index.`);
      } else if (Array.isArray(q.options) && (q.correct < 0 || q.correct >= q.options.length)) {
        errors.push(
          `Question #${qNum}: 'correct' index ${q.correct} is out of range [0, ${q.options.length - 1}].`
        );
      }

      // Difficulty
      if (!["Easy", "Medium", "Hard"].includes(q.difficulty)) {
        errors.push(
          `Question #${qNum}: 'difficulty' must be exactly 'Easy', 'Medium', or 'Hard' (got '${q.difficulty}').`
        );
      }

      // Explanation warning
      if (typeof q.explanation !== "string" || !q.explanation.trim()) {
        warnings.push(`Question #${qNum}: 'explanation' is empty.`);
      }

      // PassageKey
      if (q.passageKey !== undefined && q.passageKey !== null) {
        if (typeof q.passageKey !== "string" || !q.passageKey.trim()) {
          errors.push(`Question #${qNum}: 'passageKey' must be null or a non-empty string.`);
        } else if (!validPassageKeys.has(q.passageKey.trim())) {
          errors.push(
            `Question #${qNum}: 'passageKey' '${q.passageKey}' does not exist in 'passages' list.`
          );
        }
      }

      // Question-level reviewFlags
      if (Array.isArray(q.reviewFlags)) {
        q.reviewFlags.forEach((qf: any) => {
          if (typeof qf === "string" && qf.trim()) {
            const tag = `Q${qNum}: ${qf.trim()}`;
            if (!reviewFlags.includes(tag)) reviewFlags.push(tag);
          }
        });
      }
    });
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    reviewFlags,
    questionsCount: Array.isArray(rawQs) ? rawQs.length : 0,
    notesCount,
    data,
  };
}
